/* BoTT Newsletter Popup.
   Opens after a configurable delay (section setting) on home page visits.
   Closing it snoozes it for a configurable number of days (section setting,
   0 = every visit); a successful subscribe hides it permanently (logged-in
   customers who already accept marketing are excluded in Liquid, but guests
   are anonymous — localStorage is the only way to remember either state).
   `form.posted_successfully?` / `form.errors` are page-global across every
   {% form 'customer' %} on the page (the footer newsletter also renders on the
   home page), so a sessionStorage flag set on this popup's own submit is what
   attributes the reloaded page's success/error to the popup. */

(() => {
  const SUBSCRIBED_KEY = 'bott-newsletter-popup:subscribed';
  const SUBMITTED_KEY = 'bott-newsletter-popup:submitted';
  const DISMISSED_KEY = 'bott-newsletter-popup:dismissed-at';

  /* Storage can throw (private browsing, sandboxed editor iframes); treat a
     failure as "no flag" so the popup still behaves sensibly. */
  function getStorage(store, key) {
    try {
      return store.getItem(key);
    } catch {
      return null;
    }
  }

  function setStorage(store, key, value) {
    try {
      store.setItem(key, value);
    } catch {
      /* noop */
    }
  }

  function removeStorage(store, key) {
    try {
      store.removeItem(key);
    } catch {
      /* noop */
    }
  }

  class BottNewsletterPopup extends HTMLElement {
    constructor() {
      super();

      this.querySelector('.bott-newsletter-popup__close').addEventListener('click', () => this.close());
      this.addEventListener('keyup', (event) => {
        if (event.code && event.code.toUpperCase() === 'ESCAPE') this.close();
      });
      this.addEventListener('click', (event) => {
        if (event.target === this) this.close();
      });
      // Flag set before the native POST navigates away; read after the reload.
      this.querySelector('form')?.addEventListener('submit', () => {
        setStorage(sessionStorage, SUBMITTED_KEY, '1');
      });

      this.onSectionSelect = (event) => {
        if (event.detail.sectionId === this.dataset.section) this.open();
      };
      this.onSectionDeselect = (event) => {
        if (event.detail.sectionId === this.dataset.section) this.close();
      };
    }

    connectedCallback() {
      if (this.moved) return;
      this.moved = true;
      // Move to <body> so no ancestor stacking context can trap the overlay.
      // data-section keeps theme-editor.js's section:unload cleanup working.
      this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
      document.body.appendChild(this);

      if (window.Shopify && window.Shopify.designMode) {
        // Editor: open/close follows section selection only — no timer, no storage.
        document.addEventListener('shopify:section:select', this.onSectionSelect);
        document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
        return;
      }

      if (getStorage(localStorage, SUBSCRIBED_KEY)) return;

      const success = this.querySelector('.bott-newsletter-popup__message--success');
      const error = this.querySelector('.bott-newsletter-popup__message--error');
      const submittedHere = getStorage(sessionStorage, SUBMITTED_KEY);
      if (submittedHere) removeStorage(sessionStorage, SUBMITTED_KEY);

      if (success) {
        // Subscribed — via this popup or the footer form; either way, done forever.
        setStorage(localStorage, SUBSCRIBED_KEY, '1');
        if (submittedHere) this.open();
        return;
      }

      if (error) {
        // Reopen immediately so the visitor sees the message they caused;
        // a footer-form error shouldn't pop this up over them.
        if (submittedHere) this.open();
        return;
      }

      /* Dismissal snooze — computed against the current setting so the
         merchant changing the day count applies to past dismissals too. */
      const snoozeDays = Number(this.dataset.snoozeDays) || 0;
      const dismissedAt = Number(getStorage(localStorage, DISMISSED_KEY)) || 0;
      if (dismissedAt && Date.now() - dismissedAt < snoozeDays * 86400000) return;

      const delay = Number(this.dataset.delay) || 0;
      this.timer = setTimeout(() => this.openWhenIdle(), delay * 1000);
    }

    /* Don't steal focus while the visitor is typing (footer newsletter,
       predictive search, …) — retry until they leave the field. */
    openWhenIdle() {
      const active = document.activeElement;
      if (active && active.matches('input, textarea, select, [contenteditable="true"]')) {
        this.timer = setTimeout(() => this.openWhenIdle(), 3000);
        return;
      }
      this.open();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      document.removeEventListener('shopify:section:select', this.onSectionSelect);
      document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
    }

    open() {
      if (this.hasAttribute('open')) return;
      this.openedBy = document.activeElement;
      this.setAttribute('open', '');
      document.body.classList.add('overflow-hidden');
      const focusTarget =
        this.querySelector('.bott-newsletter-popup__message--success') || this.querySelector('[role="dialog"]');
      trapFocus(this, focusTarget);
    }

    close() {
      if (!this.hasAttribute('open')) return;
      this.removeAttribute('open');
      document.body.classList.remove('overflow-hidden');
      removeTrapFocus(this.openedBy);
      // Editor closes (section deselect) must not snooze the merchant's preview.
      if (!(window.Shopify && window.Shopify.designMode)) {
        setStorage(localStorage, DISMISSED_KEY, String(Date.now()));
      }
    }
  }

  customElements.define('bott-newsletter-popup', BottNewsletterPopup);
})();
